import {
  BodyText,
  Field,
  FieldError,
  FieldLabel,
  Input,
} from "@/components/ui";
import { Search } from "@/components/ui";
import { TRACKERS } from "@/constants/Trackers";
import { trackEvent } from "@/lib/tracking";

import { zodResolver } from "@hookform/resolvers/zod";

import { SubmitCancelButtons } from "@/features/Patient/MedicalRecords/components/SubmitCancelButtons";
import { Coverage } from "@curai/js-fhir-tools";
import { useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import type { z } from "zod";

import {
  type HealthInsuranceCoverage,
  useBillingInsuranceCarriers,
} from "../network/useHealthInsurance";
import { insuranceFormSchema } from "../schemas/schemas";

interface HealthInsuranceFormProps {
  onSubmit(
    values: z.infer<typeof insuranceFormSchema>,
    existingCoverage?: HealthInsuranceCoverage,
  ): void;
  onCancel(): void;
  isSaving?: boolean;
  existingCoverage?: HealthInsuranceCoverage;
}

export const HealthInsuranceForm = ({
  onSubmit,
  onCancel,
  isSaving = false,
  existingCoverage = undefined,
}: HealthInsuranceFormProps) => {
  const [carrierNameSearchTerm, setCarrierNameSearchTerm] = useState<string>(
    existingCoverage?.payorOrganizationName || "",
  );
  const memberIdRef = useRef<HTMLInputElement>(null);
  const x = Object.entries(Coverage.SubscriberRelationship.values).find(
    ([, value]) => value === existingCoverage?.subscriberRelationship,
  )?.[0];

  const subscriberRelationshipDisplay = x
    ? Coverage.SubscriberRelationship.display[
        x as keyof typeof Coverage.SubscriberRelationship.values
      ]
    : "";

  const [policyHolderSearchTerm, setPolicyHolderSearchTerm] = useState<string>(
    subscriberRelationshipDisplay || "Self",
  );
  const { data: billingInsuranceCarriers } = useBillingInsuranceCarriers();
  const carriers = useMemo(() => {
    return billingInsuranceCarriers?.map((carrier) => ({
      label: carrier.displayName || "",
      value: carrier.id || "",
    }));
  }, [billingInsuranceCarriers]);

  const policyHolderOptions = useMemo(() => {
    return [
      {
        label: "Self",
        value: "self",
      },
      {
        label: "Spouse",
        value: "spouse",
      },
      {
        label: "Child",
        value: "child",
      },
      {
        label: "Other",
        value: "other",
      },
    ];
  }, []);
  const form = useForm<z.infer<typeof insuranceFormSchema>>({
    resolver: zodResolver(insuranceFormSchema),
    defaultValues: {
      payorOrganizationId: existingCoverage?.payorOrganizationId || "",
      planCode: existingCoverage?.planCode || "",
      memberId: existingCoverage?.memberIdentifier || "",
      groupNumber: existingCoverage?.groupNumber || "",
      customerServicePhoneNumber:
        existingCoverage?.customerServicePhoneNumber || "",
      subscriberRelationship:
        existingCoverage?.subscriberRelationship || "self",
    },
  });

  const handleSubmit = (values: z.infer<typeof insuranceFormSchema>) => {
    onSubmit(
      {
        payorOrganizationId: values.payorOrganizationId,
        planCode: values.planCode,
        memberId: values.memberId,
        groupNumber: values.groupNumber,
        customerServicePhoneNumber: values.customerServicePhoneNumber,
        subscriberRelationship: values.subscriberRelationship,
      },
      existingCoverage,
    );
  };

  return (
    <form
      onSubmit={form.handleSubmit(handleSubmit)}
      className="flex flex-col gap-2"
    >
      <div className="flex flex-row gap-2">
        <div className="flex flex-col flex-1">
          <Controller
            control={form.control}
            name="payorOrganizationId"
            render={({ fieldState }) => (
              <Field className="w-full">
                <FieldLabel htmlFor="payorOrganizationId">
                  <BodyText bold size="sm">
                    *Insurance Carrier
                  </BodyText>
                </FieldLabel>
                <Search
                  placeholder="Search for insurance carrier"
                  input={carrierNameSearchTerm}
                  options={carriers || []}
                  onInputChange={(input) => {
                    setCarrierNameSearchTerm(input);
                  }}
                  onItemSelected={(item) => {
                    form.setValue("payorOrganizationId", item.value.toString());
                    memberIdRef.current?.focus();
                  }}
                  manualFilter={(options, searchString) => {
                    return options.filter((option) =>
                      option.label
                        .toLowerCase()
                        .startsWith(searchString.toLowerCase()),
                    );
                  }}
                  maxOptions={25}
                  openOnFocus
                  openWithoutInput
                />
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />
        </div>
        <div className="flex flex-col flex-1">
          <Controller
            control={form.control}
            name="memberId"
            render={({ field, fieldState }) => (
              <Field className="w-full">
                <FieldLabel htmlFor="memberId">
                  <BodyText size="sm" bold>
                    *Member ID
                  </BodyText>
                </FieldLabel>
                <Input {...field} ref={memberIdRef} id="memberId" />
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />
        </div>
      </div>
      <div className="flex flex-row gap-2">
        <div className="flex flex-col flex-1">
          <Controller
            control={form.control}
            name="planCode"
            render={({ field, fieldState }) => (
              <Field className="w-full">
                <FieldLabel htmlFor="planCode">
                  <BodyText size="sm" bold>
                    Plan
                  </BodyText>
                </FieldLabel>
                <Input {...field} id="planCode" />
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />
        </div>
        <div className="flex flex-col flex-1">
          <Controller
            control={form.control}
            name="groupNumber"
            render={({ field, fieldState }) => (
              <Field className="w-full">
                <FieldLabel htmlFor="groupNumber">
                  <BodyText size="sm" bold>
                    Group Number
                  </BodyText>
                </FieldLabel>
                <Input {...field} id="groupNumber" />
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />
        </div>
      </div>
      <div className="flex flex-row gap-2">
        <div className="flex flex-col flex-1">
          <Controller
            control={form.control}
            name="customerServicePhoneNumber"
            render={({ field, fieldState }) => (
              <Field className="w-full">
                <FieldLabel htmlFor="customerServicePhoneNumber">
                  <BodyText size="sm" bold>
                    *Customer Service Number
                  </BodyText>
                </FieldLabel>
                <Input
                  {...field}
                  id="customerServicePhoneNumber"
                  maxLength={16}
                />
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />
        </div>
        <div className="flex flex-col flex-1">
          <Controller
            control={form.control}
            name="subscriberRelationship"
            render={({ fieldState }) => (
              <Field className="w-full">
                <FieldLabel htmlFor="subscriberRelationship">
                  <BodyText bold size="sm">
                    Relationship to Policy Holder
                  </BodyText>
                </FieldLabel>
                <Search
                  placeholder="Search for policy holder"
                  input={policyHolderSearchTerm}
                  options={policyHolderOptions}
                  onInputChange={(input) => {
                    setPolicyHolderSearchTerm(input);
                  }}
                  onItemSelected={(item) => {
                    form.setValue(
                      "subscriberRelationship",
                      item.value.toString(),
                    );
                  }}
                  shouldFilter={false}
                  openOnFocus
                  openWithoutInput
                />
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />
        </div>
      </div>

      <SubmitCancelButtons
        isSaving={isSaving}
        onCancel={() => {
          trackEvent(TRACKERS.CANCEL_INSURANCE);
          onCancel();
        }}
        onSave={() => {
          trackEvent(TRACKERS.SAVE_INSURANCE);
        }}
      />
    </form>
  );
};
